import { type ReactElement, useMemo } from 'react';

import {
  EnumFilterEditor,
  type FilterEditorComponentProps,
} from '@spotify/confidence-core-react';

import { type WorkflowExtensionOptions } from '../../extensions';
import { useWorkflowExtensions } from '../../hooks';

export const normalizeTypeId = (id: string): string =>
  id === 'reanalysis' ? 'analysis' : id;

export const TypeFilterEditor = (
  props: FilterEditorComponentProps<string[]> & {
    excludeWorkflowIds?: string[];
  },
): ReactElement => {
  const workflowExtensions = useWorkflowExtensions();
  const { excludeWorkflowIds, ...editorProps } = props;

  const visibleExtensions = useMemo(() => {
    let exts = workflowExtensions;
    if (excludeWorkflowIds?.length) {
      const excluded = new Set(excludeWorkflowIds);
      exts = exts.filter(w => !excluded.has(w.workflowId));
    }
    const ids = new Set(exts.map(w => w.workflowId));
    const deduped =
      ids.has('analysis') && ids.has('reanalysis')
        ? exts.filter(w => w.workflowId !== 'reanalysis')
        : exts;
    return [...deduped].sort((a, b) => a.title.localeCompare(b.title));
  }, [workflowExtensions, excludeWorkflowIds]);

  const extensionsByTypeId = new Map<string, WorkflowExtensionOptions>();
  for (const extension of visibleExtensions) {
    const typeId = normalizeTypeId(extension.workflowId);
    if (!extensionsByTypeId.has(typeId))
      extensionsByTypeId.set(typeId, extension);
  }

  return (
    <EnumFilterEditor
      {...editorProps}
      options={[...extensionsByTypeId].map(([value, extension]) => ({
        value,
        label: extension.title,
        icon: extension.workflowIcon,
      }))}
    />
  );
};
